import type { RequiredDocumentTypeInput } from '@chukua/common';
import { Alert, Button, Drawer, Form, Input } from 'antd';
import { useEffect } from 'react';
import type { RequiredDocumentType } from '../../../reference-data.api';

interface RequiredDocumentTypeFormDrawerProps {
  open: boolean;
  /** Present to edit that row; absent to create a new one. */
  editing?: RequiredDocumentType;
  error: string | null;
  submitting: boolean;
  onSubmit: (input: RequiredDocumentTypeInput) => Promise<boolean>;
  onClose: () => void;
}

/** One drawer for both create and edit — "edit every field" means there's no separate patch shape. */
export function RequiredDocumentTypeFormDrawer({ open, editing, error, submitting, onSubmit, onClose }: RequiredDocumentTypeFormDrawerProps) {
  const [form] = Form.useForm<RequiredDocumentTypeInput>();

  useEffect(() => {
    if (open) form.setFieldsValue(editing ?? { code: '', name: '' });
  }, [open, editing, form]);

  async function submit(values: RequiredDocumentTypeInput) {
    const saved = await onSubmit(values);
    if (saved) onClose();
  }

  return (
    <Drawer open={open} onClose={onClose} title={editing ? 'Edit required document' : 'New required document'} placement="right" size={420} destroyOnClose>
      {error && <Alert className="mb-5" type="error" title={error} showIcon />}
      <Form<RequiredDocumentTypeInput> form={form} layout="vertical" requiredMark={false} onFinish={submit} disabled={submitting}>
        <Form.Item name="code" label="Code" rules={[{ required: true, message: 'Enter a code' }]}>
          <Input className="font-mono" autoFocus />
        </Form.Item>
        <Form.Item name="name" label="Name" rules={[{ required: true, message: 'Enter a name' }]}>
          <Input />
        </Form.Item>
        <Form.Item className="mb-0 pt-2">
          <Button type="primary" htmlType="submit" loading={submitting}>
            {editing ? 'Save' : 'Create required document'}
          </Button>
        </Form.Item>
      </Form>
    </Drawer>
  );
}
